import React, {
	useEffect,
	useState,
	useRef,
	useMemo,
	useCallback,
} from "react";
import { Button, Divider, Result, Select } from "antd";
import DefaultInput from "../../components/form/DefaultInput";
import api from "../../utils/axios";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import DefaultSelect from "../../components/form/DefaultSelect";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L, { Icon, LatLngExpression, LatLngTuple } from "leaflet";
import "./index.css";
import { fetchZonesCached } from "../../lib/cachedLookups";
import classes from "./RegisterBuyerForm.module.css";
import useLang from "../../hooks/useLang";
import { CONFIG } from "../../config";
import AddressSelect from "../../components/form/AddressSelect";

const { Option } = Select;

const NOMINATIM_BASE_URL = "https://nominatim.openstreetmap.org/search?";

const RegisterBuyerForm = () => {
	const { uuid } = useParams();
	const { strings } = useLang("registerBuyerForm");

	const [name, setName] = useState<string | undefined>();
	const [lastName, setLastName] = useState<string | undefined>();
	const [phone, setPhone] = useState<string | undefined>();
	const [address, setAddress] = useState<string | undefined>();
	const [country, setCountry] = useState<string | undefined>();
	const [city, setCity] = useState<string | undefined>();
	const [temporaryLinkValidationData, setTemporaryLinkValidationData] =
		useState<any>([]);
	const [countryDropDown, setCountryDropDown] = useState<any>([]);
	const [cityDropDown, setCityDropDown] = useState<any>([]);
	const [cities, setCities] = useState<any>([]);

	const [loading, setLoading] = useState<boolean>(false);
	const [submited, setSubmited] = useState<boolean>(false);
	const [completed, setCompleted] = useState<boolean>(false);
	const [latitude, setLatitude] = useState<number | null>(null);
	const [longitude, setLongitude] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [mapCenter, setMapCenter] = useState<LatLngTuple | null>(null);
	const markerRef = useRef(null);
	const [position, setPosition] = useState<any>(null);
	const [adressPosition, setAddressPosition] = useState<any>(null);
	const [sectionId, setSectionId] = useState<number>();
	const [finalAddress, setFinalAddress] = useState<string>("");

	const [apiLatitude, setApiLatitude] = useState<number | null>();
	const [apiLongitude, setApiLongitude] = useState<number | null>();
	const [countryLabel, setCountryLabel] = useState<string>("xk");
	const [cityLabel, setCityLabel] = useState<string | undefined>("");

	useEffect(() => {
		// Check if the Geolocation API is supported by the browser
		if ("geolocation" in navigator) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					setLatitude(position.coords.latitude);
					setLongitude(position.coords.longitude);
					setMapCenter([
						position.coords.latitude,
						position.coords.longitude,
					]);
					setPosition([
						position.coords.latitude,
						position.coords.longitude,
					]);
				},
				(error) => {
					setLatitude(42.6026);
					setLongitude(20.903);
					setPosition([42.6026, 20.903]);
					setMapCenter([42.6026, 20.903]);
					setError(`Error getting location: ${error.message}`);
				}
			);
		} else {
			setLatitude(42.6026);
			setLongitude(20.903);
			setMapCenter([42.6026, 20.903]);
			// setError("Geolocation is not supported by your browser");
		}
		if (latitude !== null && longitude !== null) {
			setMapCenter([latitude, longitude]);
		}
		const position = getValidCoordinates(latitude, longitude);
		setPosition(position);
		// On mount the coordinates are usually still empty (the location arrives later and is
		// looked up then), and a lookup without them only returns an error.
		if (position) fetchGeoData(latitude, longitude);
	}, []);

	const getValidCoordinates = (
		lat: number | null,
		lng: number | null
	): LatLngExpression | null => {
		if (lat !== null && lng !== null) {
			return [lat, lng];
		}
		return null;
	};

	const customIcon = new Icon({
		iconUrl: require("../../assets/pin.png"),
		iconSize: [38, 38],
	});

	const [geoData, setGeoData] = useState<any>();

	const fetchGeoData = useCallback(async (latitude: any, longitude: any) => {
		try {
			const response = await api.get("External/get-geo-data", {
				params: {
					lng: longitude,
					lat: latitude,
					category: CONFIG?.zoneShofer ?? "",
				},
			});
			if (response.status === 200) {
				const result = response?.data;
				setSectionId(result.geofences[0]?.name as number);
				// setFinalAddress(result.result);
				setGeoData(response.data);
			}
		} catch (error) {
			console.log(error);
		}
	}, []);

	const eventHandlers = useMemo(
		() => ({
			dragend() {
				const marker = markerRef.current;
				if (marker != null) {
					const latLng = marker["_latlng"];
					setPosition(latLng);
					setApiLatitude(latLng["lat"]);
					setApiLongitude(latLng["lng"]);
					fetchGeoData(latLng["lat"], latLng["lng"]);
				}
			},
		}),
		[]
	);

	//Form
	useEffect(() => {
		const checkLinkValidation = async () => {
			const zones = await fetchZonesCached();
			if (zones) {
				setCountriesAndCities(zones);
			}
			setLoading(true);
			try {
				const response = await api.get(
					"Buyer/temporary-link-validation",
					{ params: { uuid: uuid } }
				);
				if (response) {
					const results = response?.data;
					setTemporaryLinkValidationData(response?.data);
					results.count === 1
						? setCompleted(true)
						: setCompleted(false);
				} else {
					setTemporaryLinkValidationData([]);
				}
			} catch (error) {
				console.log(error);
				setTemporaryLinkValidationData([]);
			} finally {
				setLoading(false);
			}
		};
		checkLinkValidation();
	}, []);

	const setCountriesAndCities = (zones: any) => {
		if (zones) {
			const countries = zones
				.filter((item: any) => item.levelId === 1)
				.map((item: any) => ({
					value: item.id,
					label: item.description,
				}));
			setCountryDropDown(countries);
			const filteredCities = zones
				.filter((item: any) => item.levelId === 2)
				.map((item: any) => ({
					value: item.id,
					label: item.description,
				}));
			setCityDropDown(filteredCities);
			const cities = zones?.filter((item: any) => item.levelId === 2);
			setCities(cities);
		}
	};

	const validateBuyerData = (data: any) => {
		const { firstName, lastName, sectionId, address, phone } = data;
		if (!firstName) {
			toast.error(strings?.toastMessages.firstNameRequired);
			return false;
		}
		if (!lastName) {
			toast.error(strings?.toastMessages.lastNameRequired);
			return false;
		}
		if (!address) {
			toast.error(strings?.toastMessages.addressRequired);
			return false;
		}
		if (!phone) {
			toast.error(strings?.toastMessages.phoneRequired);
			return false;
		}
		if (!sectionId) {
			toast.error(strings?.toastMessages.sectionIdRequired);
			return false;
		}
		return true;
	};

	const InsertBuyer = async () => {
		const buyer = {
			firstName: name,
			lastName: lastName,
			sectionId: sectionId,
			phone: phone,
			address: address,
			uuid: uuid,
			sellerId: temporaryLinkValidationData?.sellerId,
			completed: completed,
			latitude: apiLatitude?.toString(),
			longitude: apiLongitude?.toString(),
			countryId: country,
		};
		if (validateBuyerData(buyer)) {
			try {
				setLoading(true);
				const response = await api.post("Buyer/insert-buyer", buyer);
				if (response.status === 200) {
					toast.success(strings?.toastMessages.success);
					setSubmited(true);
					setTemporaryLinkValidationData([]);
					console.log("Account created successfully!");
				}
			} catch (error) {
				console.error("Error creating account:", error);
				toast.error("Error");
			} finally {
				setLoading(false);
			}
		} else {
			return;
		}
	};

	const onCountrySelect = (e: any) => {
		const filteredCities = cities
			.filter((item: any) => item.parentId === e)
			.map((item: any) => ({
				value: item.id,
				label: item.description,
			}));
		setCityDropDown(filteredCities);
		setAddress("");
		setCity("");
		// //(autocompleteRef as any).current.handleClear();
		setCountry(e);
		if (e === 1) {
			setMapCenter([42.6026, 20.903]);
			setPosition(null);
			setCountryLabel("xk");
		} else if (e === 2) {
			setMapCenter([41.1533, 20.1683]);
			setPosition(null);
			setCountryLabel("al");
		} else if (e === 3) {
			setMapCenter([41.6086, 21.7453]);
			setPosition(null);
			setCountryLabel("mk");
		}
		setCity("");
	};

	const onCitySelect = (value: any) => {
		if (value?.value) {
			const id = value.value;
			setCity(id);
			setCityLabel(value?.label);
			setAddress("");
		} else {
			setCity("");
			setCity("");
			setAddress("");
		}
	};

	const handleChange = (address: any) => {
		const selectedLocation = address;
		if (selectedLocation) {
			const lat = selectedLocation.lat;
			const lng = selectedLocation.lng;
			setAddressPosition([lat, lng]);
			setPosition([lat, lng]);
			setApiLatitude(lat);
			setApiLongitude(lng);
			fetchGeoData(lat, lng);
			setAddress(selectedLocation.label ?? "");
		}
	};

	const ResetCenterView = (props: any) => {
		const { selectedPosition } = props;
		const { addressPosition } = props;
		const map = useMap();
		useEffect(() => {
			if (selectedPosition === 1) {
				map.setView(L.latLng(42.6026, 20.903), map.getZoom(), {
					animate: true,
				});
			} else if (selectedPosition === 2) {
				map.setView(L.latLng(41.1533, 20.1683), map.getZoom(), {
					animate: true,
				});
			} else if (selectedPosition === 3) {
				map.setView(L.latLng(41.6086, 21.7453), map.getZoom(), {
					animate: true,
				});
			}
			if (addressPosition) {
				map.setView(L.latLng(addressPosition), 15, {
					animate: true,
				});
			}
		}, [addressPosition, map, selectedPosition]);

		return null;
	};

	return (
		<>
			{temporaryLinkValidationData &&
			temporaryLinkValidationData.count > 0 &&
			!loading ? (
				<div className={classes.container}>
					<div className={classes.heroContainer}>
						<div className={classes.divTitle}>
							<h1 className="title">
								{strings?.headings.registerBuyer}
							</h1>
						</div>
						<div className={classes.inputsContainer}>
							<div className={classes.inputsDiv}>
								{/* <div className={classes.inputsHero}> */}
								<DefaultInput
									className="registerInput"
									label={strings?.labels.firstName}
									placeholder={strings?.labels.typeFirstName}
									value={name}
									onChange={(e: any) =>
										setName(e.target.value)
									}
								/>
								<DefaultInput
									className="registerInput"
									label={strings?.labels.lastName}
									placeholder={strings?.labels.typeLastName}
									value={lastName}
									onChange={(e: any) =>
										setLastName(e.target.value)
									}
								/>
								{/* </div> */}
								{/* <div className={classes.inputsHero}> */}
								<DefaultSelect
									className="registerInput"
									label={strings?.labels.country}
									flexDirection="col"
									alignCenter="start"
									placeholder={strings?.labels.select}
									options={countryDropDown}
									value={country}
									onChange={onCountrySelect}
								/>
								<DefaultSelect
									className="registerInput"
									label={strings?.labels.city}
									flexDirection="col"
									alignCenter="start"
									placeholder={strings?.labels.select}
									options={cityDropDown}
									value={city}
									onChange={onCitySelect}
									labelInValue={true}
								/>
								<div className={classes.addressDiv}>
									<p>{strings?.labels.address}:</p>

									<AddressSelect
										onAddressChange={handleChange}
										country={countryLabel}
										selectedAddress={address}
										city={city}
									/>
								</div>

								<DefaultInput
									className="registerInput"
									label={strings?.labels.phone}
									placeholder={
										strings?.labels.phonePlaceholder
									}
									value={phone}
									onChange={(e: any) =>
										setPhone(e.target.value)
									}
								/>
							</div>
							<div className={classes.mapContainer}>
								<div>
									{mapCenter !== null && (
										<MapContainer
											center={mapCenter}
											zoom={7.5}
											scrollWheelZoom={false}
											style={{
												width: 430,
												height: 450,
												borderRadius: 20,
											}}
											className={classes.map}
										>
											<TileLayer
												attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
												url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
											/>
											{position !== null && (
												<>
													<Marker
														draggable={true}
														position={position}
														icon={customIcon}
														eventHandlers={
															eventHandlers
														}
														ref={markerRef}
													>
														<Popup
															minWidth={90}
														>{`Position${position}`}</Popup>
													</Marker>
												</>
											)}
											<ResetCenterView
												selectedPosition={country}
												addressPosition={adressPosition}
											/>
										</MapContainer>
									)}
								</div>
								{(!latitude || !longitude) && (
									<p>{error || "Fetching location..."}</p>
								)}
							</div>
						</div>
						<Divider />
						<div className={classes.buttonDiv}>
							<Button
								onClick={InsertBuyer}
								size="large"
								type="primary"
								style={{
									backgroundColor: "#4F16C2",
									width: 182,
								}}
							>
								Ruaj
							</Button>
						</div>
					</div>
				</div>
			) : submited ? (
				<div className="flex justify-center items-center p-10">
					<div className="bg-white w-2/4">
						<Result
							status={"success"}
							title={strings?.resultMessages.successTitle}
						/>
					</div>
				</div>
			) : (
				<div className="flex justify-center items-center p-10">
					<div className="bg-white w-2/4">
						<Result
							status={"403"}
							title={strings?.resultMessages.linkExpiredTitle}
							subTitle={
								strings?.resultMessages.linkExpiredSubTitle
							}
						/>
					</div>
				</div>
			)}
		</>
	);
};

export default RegisterBuyerForm;
